"use client";

import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import { TemplateBrowser } from "@/components/webbuilder/features/onboarding/TemplateBrowser";
import { TemplateInfoCard } from "@/components/webbuilder/features/onboarding/TemplateInfoCard";
import { TemplatePageSelectionGrid } from "@/components/webbuilder/features/onboarding/TemplatePageSelectionGrid";
import { PageCustomizationForm } from "@/components/webbuilder/features/forms/PageCustomizationForm";
import { TemplateService } from "@/services/templateService";
import type { Template } from "@/types/models/template";
import type { CreatePageRequest } from "@/types/models/page";
import { useNavigate, useParams } from "react-router-dom";
import { usePages } from "@/hooks/usePages";
import { toast } from "sonner";
import { ArrowLeft, FilePlus2, Layers, Loader2 } from "lucide-react";

type Step = "menu" | "templates" | "pages" | "customize";

interface PageSelection {
  pageId: string;
  selected: boolean;
  customData?: {
    title: string;
    slug: string;
    description: string;
    featured_image: string;
  };
}

interface WebBuilderAddPageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after pages were created successfully (list already updated in Redux). */
  onFinishedImport?: () => void;
}

function initializePageSelections(templateData: Template): PageSelection[] {
  if (!templateData.pages?.length) return [];
  return templateData.pages.map((page) => ({
    pageId: page.id,
    selected: true,
    customData: {
      title: page.title || page.name,
      slug: page.slug,
      description: page.description || "",
      featured_image: page.featured_image_asset?.id || "",
    },
  }));
}

export function WebBuilderAddPageModal({
  open,
  onOpenChange,
  onFinishedImport,
}: WebBuilderAddPageModalProps) {
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{
    storeType: string;
    storeId: string;
  }>();
  const { actions, form } = usePages();

  const [step, setStep] = useState<Step>("menu");
  const [templateMeta, setTemplateMeta] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [template, setTemplate] = useState<Template | null>(null);
  const [pageSelections, setPageSelections] = useState<PageSelection[]>([]);
  const [showCustomization, setShowCustomization] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<Map<string, string>>(
    new Map(),
  );
  const [uploadingImages] = useState<Set<string>>(new Set());
  const [fetchingTemplate, setFetchingTemplate] = useState(false);
  const [importStarted, setImportStarted] = useState(false);

  const reset = useCallback(() => {
    setStep("menu");
    setTemplateMeta(null);
    setTemplate(null);
    setPageSelections([]);
    setShowCustomization(false);
    setUploadedImages(new Map());
    setFetchingTemplate(false);
    setImportStarted(false);
    actions.resetForm();
  }, [actions]);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  useEffect(() => {
    if (open) {
      actions.resetForm();
      setImportStarted(false);
    }
  }, [open, actions]);

  useEffect(() => {
    if (!importStarted) return;
    if (form.success && !form.loading) {
      toast.success("Pages imported successfully");
      onFinishedImport?.();
      onOpenChange(false);
      reset();
    }
    if (form.error && !form.loading) {
      toast.error(form.error.message || "Import failed");
      setImportStarted(false);
      actions.resetForm();
    }
  }, [
    importStarted,
    form.success,
    form.loading,
    form.error,
    onFinishedImport,
    onOpenChange,
    reset,
    actions,
  ]);

  const generateSlug = (title: string) =>
    title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();

  const togglePageSelection = (pageId: string) => {
    setPageSelections((prev) =>
      prev.map((s) =>
        s.pageId === pageId ? { ...s, selected: !s.selected } : s,
      ),
    );
  };

  const updateCustomData = (pageId: string, field: string, value: string) => {
    setPageSelections((prev) =>
      prev.map((s) =>
        s.pageId === pageId
          ? {
              ...s,
              customData: {
                ...s.customData!,
                [field]: value,
              },
            }
          : s,
      ),
    );
  };

  const handleTitleChange = (pageId: string, title: string) => {
    const selection = pageSelections.find((s) => s.pageId === pageId);
    const currentSlug = selection?.customData?.slug || "";
    const originalPage = template?.pages?.find((p) => p.id === pageId);
    const originalSlug = originalPage?.slug || "";
    const shouldUpdateSlug =
      currentSlug === generateSlug(selection?.customData?.title || "") ||
      currentSlug === originalSlug ||
      currentSlug === "";

    updateCustomData(pageId, "title", title);
    if (shouldUpdateSlug) {
      updateCustomData(pageId, "slug", generateSlug(title));
    }
  };

  const handleImageSelected = (
    pageId: string,
    selectedFile: { assetId: string; url: string },
  ) => {
    setUploadedImages((prev) => new Map(prev).set(pageId, selectedFile.url));
    updateCustomData(pageId, "featured_image", selectedFile.assetId);
  };

  const removeUploadedImage = (pageId: string) => {
    setUploadedImages((prev) => {
      const next = new Map(prev);
      next.delete(pageId);
      return next;
    });
    updateCustomData(pageId, "featured_image", "");
  };

  const selectedPages = pageSelections.filter((s) => s.selected);
  const needsCustomization = selectedPages.length === 1;

  const handleBlankPage = () => {
    if (!storeType || !storeId) return;
    onOpenChange(false);
    navigate(`/${storeType}/${storeId}/webbuilder/new-page`);
    reset();
  };

  const handleTemplateSelected = async (tid: string, name: string) => {
    setFetchingTemplate(true);
    try {
      const tpl = await TemplateService.getTemplateById(tid);
      setTemplateMeta({ id: tid, name });
      setTemplate(tpl);
      setPageSelections(initializePageSelections(tpl));
      setShowCustomization(false);
      setStep("pages");
    } catch {
      toast.error("Could not load template. Try again.");
    } finally {
      setFetchingTemplate(false);
    }
  };

  const handleImportContinue = () => {
    if (!templateMeta || !template) return;

    if (needsCustomization && !showCustomization) {
      setShowCustomization(true);
      return;
    }

    const selectedIds = selectedPages.map((s) => s.pageId);
    if (selectedIds.length === 0) {
      toast.error("Select at least one page");
      return;
    }

    const customizations = selectedPages.reduce(
      (acc, selection) => {
        if (selection.customData) acc[selection.pageId] = selection.customData;
        return acc;
      },
      {} as Record<
        string,
        {
          title?: string;
          slug?: string;
          description?: string;
          featured_image?: string;
        }
      >,
    );

    const isSinglePage =
      selectedIds.length === 1 && Object.keys(customizations).length > 0;

    actions.resetForm();
    setImportStarted(true);

    if (isSinglePage) {
      const pageId = selectedIds[0];
      const customData = customizations[pageId] || {
        title: "",
        slug: "",
        description: "",
        featured_image: "",
      };
      const pageData: CreatePageRequest = {
        name: customData.title || `Page from ${templateMeta.name}`,
        slug: customData.slug || `page-${Date.now()}`,
        title: customData.title || `Page from ${templateMeta.name}`,
        description: customData.description || "",
        featured_image_asset_id: customData.featured_image || undefined,
        status: "draft",
        template_id: templateMeta.id,
        template_page_id: pageId,
      };
      actions.createPageFromTemplate(pageData);
    } else {
      actions.createPagesFromTemplate(
        templateMeta.id,
        selectedIds.length > 0 ? selectedIds : undefined,
      );
    }
  };

  const backFromTemplates = () => setStep("menu");

  const backFromPages = () => {
    if (showCustomization) {
      setShowCustomization(false);
      return;
    }
    setStep("templates");
    setTemplate(null);
    setTemplateMeta(null);
    setPageSelections([]);
  };

  const dialogTitle =
    step === "menu"
      ? "Add pages"
      : step === "templates"
        ? "Choose a template"
        : showCustomization
          ? "Customize page"
          : "Select pages";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto bg-card border-border">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription className="sr-only">
            Create a blank page or import pages from a template
          </DialogDescription>
        </DialogHeader>

        {step === "menu" && (
          <div className="grid gap-3 sm:grid-cols-2 pt-2">
            <button
              type="button"
              onClick={handleBlankPage}
              className="flex flex-col items-start gap-2 rounded-xl border border-border bg-muted/40 p-4 text-left transition hover:bg-muted/70 hover:border-primary/50"
            >
              <FilePlus2 className="h-6 w-6 text-primary" />
              <span className="font-semibold text-foreground">
                Blank page
              </span>
              <span className="text-sm text-muted-foreground">
                Start from scratch with an empty page.
              </span>
            </button>
            <button
              type="button"
              onClick={() => setStep("templates")}
              className="flex flex-col items-start gap-2 rounded-xl border border-border bg-muted/40 p-4 text-left transition hover:bg-muted/70 hover:border-primary/50"
            >
              <Layers className="h-6 w-6 text-primary" />
              <span className="font-semibold text-foreground">
                From template
              </span>
              <span className="text-sm text-muted-foreground">
                Import one or more pages from a template.
              </span>
            </button>
          </div>
        )}

        {step === "templates" && (
          <div className="relative min-h-[280px]">
            {fetchingTemplate && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/80">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
            <TemplateBrowser
              embedded
              onBack={backFromTemplates}
              onSelectTemplate={handleTemplateSelected}
              onCreateBlank={handleBlankPage}
            />
          </div>
        )}

        {step === "pages" && template && templateMeta && (
          <div className="space-y-4 pt-2">
            {!showCustomization ? (
              <>
                <TemplateInfoCard template={template} />
                <TemplatePageSelectionGrid
                  template={template}
                  pageSelections={pageSelections}
                  onTogglePageSelection={togglePageSelection}
                />
              </>
            ) : (
              <PageCustomizationForm
                template={template}
                selectedPages={selectedPages}
                uploadingImages={uploadingImages}
                uploadedImages={uploadedImages}
                onTitleChange={handleTitleChange}
                onUpdateCustomData={updateCustomData}
                onImageSelected={handleImageSelected}
                onRemoveUploadedImage={removeUploadedImage}
                generateSlug={generateSlug}
              />
            )}

            <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between border-t border-border pt-4">
              <ButtonOutline type="button" onClick={backFromPages}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {showCustomization ? "Back to selection" : "Back"}
              </ButtonOutline>
              <ButtonPrimary
                type="button"
                disabled={
                  selectedPages.length === 0 ||
                  form.loading ||
                  fetchingTemplate
                }
                onClick={handleImportContinue}
              >
                {form.loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing…
                  </>
                ) : needsCustomization && !showCustomization ? (
                  "Customize"
                ) : (
                  `Import ${selectedPages.length || ""} page${selectedPages.length === 1 ? "" : "s"}`
                )}
              </ButtonPrimary>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
